import { AfterViewChecked, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, QueryList, Renderer2, ViewChild, ViewChildren } from '@angular/core';
import { navigation } from '../../../../../data/header-navigation';
import { NavigationLink } from '../../../../shared/interfaces/navigation-link';
import { DirectionService } from '../../../../shared/services/direction.service';
import { merge, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { HeaderService } from '../../../../shared/services/header.service';
import { HeaderheaderService } from '../../header.service';
import { UserSessionService } from 'src/app/services/usersession.service';

@Component({
    selector: 'app-header-links',
    templateUrl: './links.component.html',
    styleUrls: ['./links.component.scss']
})
export class LinksComponent implements OnInit, OnDestroy, AfterViewChecked {
    @ViewChildren('submenuElement') submenuElements!: QueryList<ElementRef>;
    @ViewChildren('itemElement') itemElements!: QueryList<ElementRef>;
    @ViewChild('navLinksList', { static: true }) navLinksList!: ElementRef;

    destroy$: Subject<void> = new Subject<void>();

    items: any;
    hoveredItem: NavigationLink | null = null;

    reCalcSubmenuPosition = false;
    hovered: any = false

    constructor(
        private direction: DirectionService,
        private header: HeaderService,
        private zone: NgZone,
        private renderer: Renderer2,
        private userSessionService: UserSessionService,
        private headerservice: HeaderheaderService
    ) {
        const menu = this.userSessionService.getLocalStorageWithKey('menu');
        if (menu) {
            this.items = JSON.parse(menu);
        }
    }

    onClick(item: NavigationLink, event: Event): void {
        event.stopPropagation();
        if (this.hoveredItem === item) {
            this.hoveredItem = null;
            this.hovered = false;
        } else {
            this.hoveredItem = item;
            this.hovered = true;
            this.reCalcSubmenuPosition = true;
        }

        this.headerservice.isdropdownOpen$.subscribe((res: any) => {
            this.hovered = res;
        });
    }

    onDocumentClick(event: Event): void {
        const target = event.target as HTMLElement;
        if (!this.navLinksList.nativeElement.contains(target)) {
            this.hoveredItem = null;
            this.hovered = false;
        }
    }

    onTouchClick(event: Event, item: NavigationLink): void {
        if (event.cancelable) {
            if (this.hoveredItem && this.hoveredItem === item) {
                return;
            }

            if (item.menu) {
                event.preventDefault();

                this.hoveredItem = item;
                this.reCalcSubmenuPosition = true;
            }
        }
    }

    onOutsideTouchClick(item: NavigationLink): void {
        this.hoveredItem = null;
    }

    onSubItemClick(): void {
        this.hoveredItem = null;
    }

    ngOnInit(): void {
        this.renderer.listen('document', 'click', this.onDocumentClick.bind(this));
        merge(
            this.header.navPanelPositionState$,
            this.header.navPanelVisibility$,
        ).pipe(takeUntil(this.destroy$)).subscribe(() => this.hoveredItem = null);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngAfterViewChecked(): void {
        if (!this.hovered) {
            this.hoveredItem = null;
        }
        if (!this.reCalcSubmenuPosition) {
            return;
        }

        this.reCalcSubmenuPosition = false;

        const itemElement = this.getCurrentItemElement();
        const submenuElement = this.getCurrentSubmenuElement();

        if (!submenuElement || !itemElement) {
            return;
        }

        const submenuTop = submenuElement.getBoundingClientRect().top;
        const viewportHeight = window.innerHeight;
        const paddingBottom = 20;

        submenuElement.style.maxHeight = `${viewportHeight - submenuTop - paddingBottom}px`;

        // calc megamenu position
        if (this.hoveredItem?.menu?.type !== 'megamenu') {
            return;
        }

        const container = submenuElement.offsetParent;

        if (!container) {
            return;
        }

        const containerWidth = container.getBoundingClientRect().width;
        const megamenuWidth = submenuElement.getBoundingClientRect().width;

        if (this.direction.isRTL()) {
            const itemPosition = containerWidth - (itemElement.offsetLeft + itemElement.offsetWidth);
            const megamenuPosition = Math.round(Math.min(itemPosition, containerWidth - megamenuWidth));

            submenuElement.style.right = megamenuPosition + 'px';
        } else {
            const itemPosition = itemElement.offsetLeft;
            const megamenuPosition = Math.round(Math.min(itemPosition, containerWidth - megamenuWidth));

            submenuElement.style.left = megamenuPosition + 'px';
        }
    }

    getCurrentItemElement(): HTMLDivElement | null {
        if (!this.hoveredItem) {
            return null;
        }

        const index = this.items.indexOf(this.hoveredItem);
        const elements = this.itemElements.toArray();

        if (index === -1 || !elements[index]) {
            return null;
        }

        return elements[index].nativeElement as HTMLDivElement;
    }

    getCurrentSubmenuElement(): HTMLDivElement | null {
        if (!this.hoveredItem) {
            return null;
        }

        const index = this.items.filter(x => x.menu).indexOf(this.hoveredItem);
        const elements = this.submenuElements.toArray();

        if (index === -1 || !elements[index]) {
            return null;
        }

        return elements[index].nativeElement as HTMLDivElement;
    }
}
